import {
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_SYMBOL,
  decimalPlacesFor,
  type BusinessSettingsPayload,
} from '@stockpilot/shared';

/**
 * All formatting is driven by the business's own settings — currency, locale,
 * decimals and timezone. Nothing here assumes a country.
 */
export interface FormatContext {
  currency: string;
  currencySymbol: string;
  currencyPosition: 'BEFORE' | 'AFTER';
  decimalPlaces: number;
  locale: string;
  timezone: string;
}

/** Only ever used before the business's own settings have loaded. */
export const DEFAULT_FORMAT: FormatContext = {
  currency: DEFAULT_CURRENCY,
  currencySymbol: DEFAULT_CURRENCY_SYMBOL,
  currencyPosition: 'BEFORE',
  decimalPlaces: decimalPlacesFor(DEFAULT_CURRENCY),
  locale: 'en',
  timezone: 'UTC',
};

export function formatContextFrom(settings?: BusinessSettingsPayload | null): FormatContext {
  if (!settings) return DEFAULT_FORMAT;
  return {
    currency: settings.currency,
    currencySymbol: settings.currencySymbol,
    currencyPosition: settings.currencyPosition,
    decimalPlaces: settings.decimalPlaces,
    locale: settings.locale || 'en',
    timezone: settings.timezone || 'UTC',
  };
}

/** Money arrives from the API as a decimal STRING; never parse it into a float twice. */
export function formatMoney(
  value: string | number | null | undefined,
  ctx: FormatContext = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (!Number.isFinite(amount)) return '—';

  const body = new Intl.NumberFormat(ctx.locale, {
    minimumFractionDigits: ctx.decimalPlaces,
    maximumFractionDigits: ctx.decimalPlaces,
  }).format(amount);

  return ctx.currencyPosition === 'BEFORE'
    ? `${ctx.currencySymbol}${body}`
    : `${body}${ctx.currencySymbol}`;
}

export function formatQuantity(
  value: string | number | null | undefined,
  ctx: FormatContext = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (!Number.isFinite(amount)) return '—';

  // Whole numbers read better without trailing zeros; fractions keep 3 places.
  return new Intl.NumberFormat(ctx.locale, {
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 3,
  }).format(amount);
}

export function formatNumber(value: number, locale = 'en'): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatDate(
  value: string | Date | null | undefined,
  ctx: FormatContext = DEFAULT_FORMAT,
): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(ctx.locale, {
    dateStyle: 'medium',
    timeZone: ctx.timezone,
  }).format(date);
}

export function formatDateTime(
  value: string | Date | null | undefined,
  ctx: FormatContext = DEFAULT_FORMAT,
): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(ctx.locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: ctx.timezone,
  }).format(date);
}

export function formatRelative(value: string | Date | null | undefined, locale = 'en'): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000], ['month', 2592000], ['week', 604800],
    ['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1],
  ];

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, secondsInUnit] of units) {
    if (Math.abs(seconds) >= secondsInUnit || unit === 'second') {
      return formatter.format(Math.round(seconds / secondsInUnit), unit);
    }
  }
  return '—';
}

export function initials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

/**
 * A date as `YYYY-MM-DD` in the reader's own day, for date inputs.
 *
 * `toISOString()` is UTC, so at 00:30 in Kampala it returns yesterday — which
 * is how a day-end screen ends up balancing the wrong day for anyone east of
 * UTC in the early hours.
 */
export function isoDate(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
